/**
 * Notification API utilities
 * Handles all notification-related API calls
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/";

/**
 * Get authorization headers with JWT token
 */
const getAuthHeaders = (token) => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${token}`
});

/**
 * Fetch all notifications for the authenticated user
 * @param {string} token - JWT access token
 * @param {object} options - Query parameters (unread_only, type, limit)
 * @returns {Promise<{notifications: Array, unread_count: number}>}
 */
export const fetchNotifications = async (token, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.unread_only) params.append('unread_only', 'true');
    if (options.type) params.append('type', options.type);
    if (options.limit) params.append('limit', options.limit);

    const url = `${API_BASE_URL}api/notifications/?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(token)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

/**
 * Mark a single notification as read
 * @param {string} token - JWT access token
 * @param {string} notificationId - Notification UUID
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const markNotificationAsRead = async (token, notificationId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}api/notifications/${notificationId}/mark-read/`,
      {
        method: 'POST',
        headers: getAuthHeaders(token)
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 * @param {string} token - JWT access token
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const markAllNotificationsAsRead = async (token) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}api/notifications/mark-all-read/`,
      {
        method: 'POST',
        headers: getAuthHeaders(token)
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Delete (soft delete) a notification
 * @param {string} token - JWT access token
 * @param {string} notificationId - Notification UUID
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deleteNotification = async (token, notificationId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}api/notifications/${notificationId}/delete/`,
      {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};
